import { createContext, useContext } from 'react';

export interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  valid: boolean;
  required: boolean;
  disabled: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Props a control spreads from its enclosing FormField: id (label association), aria-describedby
 * (hint + message), aria-invalid, required, disabled. Explicit props on the control win.
 */
export function useFieldControl(own: {
  id?: string | undefined;
  'aria-describedby'?: string | undefined;
  invalid?: boolean | undefined;
  valid?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
}) {
  const field = useContext(FieldContext);
  const invalid = own.invalid ?? field?.invalid ?? false;
  const valid = !invalid && (own.valid ?? field?.valid ?? false);
  const describedBy =
    [field?.describedBy, own['aria-describedby']].filter(Boolean).join(' ') || undefined;
  return {
    id: own.id ?? field?.id,
    'aria-describedby': describedBy,
    'aria-invalid': invalid || undefined,
    required: own.required ?? field?.required,
    disabled: own.disabled ?? field?.disabled,
    invalid,
    valid,
  };
}
